import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import PropTypes from "prop-types";


export const POS_ACTIVITY_HOST_STATES =
  Object.freeze({
    LOADING: "loading",
    NO_ACTIVITY: "no_activity",
    UNSUPPORTED_ACTIVITY:
      "unsupported_activity",
    NO_ACTIVE_PUBLICATION:
      "no_active_publication",
    INVALID_PUBLICATION:
      "invalid_publication",
    PERMISSION_DENIED:
      "permission_denied",
    FEATURE_UNAVAILABLE:
      "feature_unavailable",
    SESSION_CLOSED:
      "session_closed",
    RUNTIME_ERROR:
      "runtime_error",
    READY: "ready",
  });


const EMPTY_ARRAY = Object.freeze([]);


const DEFAULT_STATE_MESSAGES =
  Object.freeze({
    loading:
      "Chargement de l’activité…",

    no_activity:
      "Aucune activité sélectionnée.",

    unsupported_activity:
      "Cette activité n’est pas prise en charge.",

    no_active_publication:
      "Aucune publication active n’est disponible.",

    invalid_publication:
      "La publication active est invalide ou incompatible.",

    permission_denied:
      "Vous ne disposez pas des permissions nécessaires.",

    feature_unavailable:
      "Cette fonctionnalité n’est pas disponible.",

    session_closed:
      "La session de caisse est fermée.",

    runtime_error:
      "Une erreur Runtime empêche le chargement de l’activité.",
  });


/**
 * Generic React host for a resolved POSActivity domain.
 *
 * Responsibilities:
 *
 * - consume POSActivityDomainResolver;
 * - manage async React lifecycle;
 * - reject stale resolution results;
 * - materialize presentation capabilities exposed
 *   by POSDomainAdapter;
 * - render deterministic generic states.
 *
 * Explicitly excluded:
 *
 * - domain resolution logic;
 * - Registry ownership;
 * - publication Runtime ownership;
 * - CashSession creation;
 * - payment;
 * - receipt;
 * - inventory mutation;
 * - accounting;
 * - NF525;
 * - business-domain fallbacks.
 */
export default function POSActivityHost({
  resolver,

  companyUuid,
  activityUuid,
  activityType,

  permissions = EMPTY_ARRAY,
  features = EMPTY_ARRAY,
  subscription = null,

  activePublication = null,

  renderState = null,
  onResolved = null,
  onError = null,
}) {
  const requestIdRef =
    useRef(0);

  const [
    state,
    setState,
  ] = useState(() =>
    createInitialState({
      companyUuid,
      activityUuid,
      activityType,
      activePublication,
    })
  );


  useEffect(() => {
    const requestId =
      requestIdRef.current + 1;

    requestIdRef.current =
      requestId;

    let active = true;


    const commit = (
      nextState
    ) => {
      if (
        !active
        || requestId
          !== requestIdRef.current
      ) {
        return;
      }

      setState(nextState);
    };


    if (
      !hasActivityScope({
        companyUuid,
        activityUuid,
        activityType,
      })
    ) {
      commit(
        createHostState(
          POS_ACTIVITY_HOST_STATES
            .NO_ACTIVITY
        )
      );

      return () => {
        active = false;
      };
    }


    if (!activePublication) {
      commit(
        createHostState(
          POS_ACTIVITY_HOST_STATES
            .NO_ACTIVE_PUBLICATION
        )
      );

      return () => {
        active = false;
      };
    }


    commit(
      createHostState(
        POS_ACTIVITY_HOST_STATES
          .LOADING
      )
    );


    Promise.resolve()
      .then(() =>
        resolver
          .resolvePOSActivityDomain({
            companyUuid,
            activityUuid,
            activityType,
            permissions,
            features,
            subscription,
            activePublication,
          })
      )
      .then(
        async (
          resolvedDomain
        ) => {
          if (
            !active
            || requestId
              !== requestIdRef.current
          ) {
            return;
          }

          const presentation =
            await materializePresentation(
              resolvedDomain
            );

          if (
            !active
            || requestId
              !== requestIdRef.current
          ) {
            return;
          }

          const readyState =
            createHostState(
              POS_ACTIVITY_HOST_STATES
                .READY,
              {
                resolvedDomain,
                presentation,
              }
            );

          commit(readyState);

          if (
            typeof onResolved
              === "function"
          ) {
            onResolved(
              resolvedDomain,
              presentation
            );
          }
        }
      )
      .catch((error) => {
        if (
          !active
          || requestId
            !== requestIdRef.current
        ) {
          return;
        }

        const hostState =
          mapErrorToHostState(
            error
          );

        commit(
          createHostState(
            hostState,
            {
              error,
            }
          )
        );

        if (
          typeof onError
            === "function"
        ) {
          onError(
            error,
            hostState
          );
        }
      });


    return () => {
      active = false;
    };
  }, [
    resolver,
    companyUuid,
    activityUuid,
    activityType,
    permissions,
    features,
    subscription,
    activePublication,
    onResolved,
    onError,
  ]);


  if (
    state.status
      !== POS_ACTIVITY_HOST_STATES
        .READY
  ) {
    if (
      typeof renderState
        === "function"
    ) {
      return renderState({
        status:
          state.status,
        error:
          state.error,
      });
    }

    return (
      <DefaultPOSActivityHostState
        status={state.status}
        error={state.error}
      />
    );
  }


  return (
    <POSActivityWorkspace
      resolvedDomain={
        state.resolvedDomain
      }
      presentation={
        state.presentation
      }
    />
  );
}


async function materializePresentation(
  resolvedDomain
) {
  if (
    !resolvedDomain
    || typeof resolvedDomain
      !== "object"
    || !resolvedDomain.adapter
  ) {
    throw createPresentationError(
      "INVALID_RESOLVED_DOMAIN",
      "ResolvedPOSActivityDomain must expose an adapter."
    );
  }

  const {
    adapter,
    runtimeConfiguration,
    publication,
  } = resolvedDomain;


  const context =
    Object.freeze({
      resolvedDomain,
      runtimeConfiguration,
      publication,
    });


  const [
    workspaceDescriptor,
    navigation,
    actions,
  ] = await Promise.all([
    Promise.resolve(
      adapter
        .getWorkspaceDescriptor(
          context
        )
    ),

    Promise.resolve(
      adapter.getNavigation(
        context
      )
    ),

    Promise.resolve(
      adapter
        .getAvailableActions(
          context
        )
    ),
  ]);


  if (
    !workspaceDescriptor
    || typeof workspaceDescriptor
      !== "object"
  ) {
    throw createPresentationError(
      "INVALID_WORKSPACE_DESCRIPTOR",
      "POSDomainAdapter.getWorkspaceDescriptor() must return an object."
    );
  }


  const component =
    await Promise.resolve(
      adapter.resolveComponent(
        workspaceDescriptor,
        context
      )
    );


  if (!component) {
    throw createPresentationError(
      "WORKSPACE_COMPONENT_UNAVAILABLE",
      "POSDomainAdapter.resolveComponent() did not return a workspace component."
    );
  }


  return Object.freeze({
    workspaceDescriptor,

    navigation:
      freezeArray(
        navigation
      ),

    actions:
      freezeArray(
        actions
      ),

    component,
  });
}


function POSActivityWorkspace({
  resolvedDomain,
  presentation,
}) {
  const {
    component:
      WorkspaceComponent,
    workspaceDescriptor,
    navigation,
    actions,
  } = presentation;


  if (
    React.isValidElement(
      WorkspaceComponent
    )
  ) {
    return WorkspaceComponent;
  }


  if (
    typeof WorkspaceComponent
      !== "function"
    && typeof WorkspaceComponent
      !== "string"
  ) {
    throw createPresentationError(
      "INVALID_WORKSPACE_COMPONENT",
      "Resolved workspace component is not renderable by React."
    );
  }


  return (
    <WorkspaceComponent
      resolvedPOSActivityDomain={
        resolvedDomain
      }
      runtimeConfiguration={
        resolvedDomain
          .runtimeConfiguration
      }
      publication={
        resolvedDomain.publication
      }
      workspaceDescriptor={
        workspaceDescriptor
      }
      navigation={navigation}
      actions={actions}
    />
  );
}


function DefaultPOSActivityHostState({
  status,
  error,
}) {
  const message =
    DEFAULT_STATE_MESSAGES[
      status
    ]
    || DEFAULT_STATE_MESSAGES
      .runtime_error;

  const isLoading =
    status
    === POS_ACTIVITY_HOST_STATES
      .LOADING;

  return (
    <section
      data-pos-activity-state={
        status
      }
      role={
        isLoading
          ? "status"
          : "alert"
      }
      aria-live="polite"
      aria-busy={
        isLoading
          ? "true"
          : "false"
      }
    >
      <p>{message}</p>

      {error?.message
        && status
          === POS_ACTIVITY_HOST_STATES
            .RUNTIME_ERROR
        ? (
          <p>
            {error.message}
          </p>
        )
        : null}
    </section>
  );
}


function createInitialState({
  companyUuid,
  activityUuid,
  activityType,
  activePublication,
}) {
  if (
    !hasActivityScope({
      companyUuid,
      activityUuid,
      activityType,
    })
  ) {
    return createHostState(
      POS_ACTIVITY_HOST_STATES
        .NO_ACTIVITY
    );
  }

  if (!activePublication) {
    return createHostState(
      POS_ACTIVITY_HOST_STATES
        .NO_ACTIVE_PUBLICATION
    );
  }

  return createHostState(
    POS_ACTIVITY_HOST_STATES
      .LOADING
  );
}


function createHostState(
  status,
  {
    error = null,
    resolvedDomain = null,
    presentation = null,
  } = {}
) {
  return {
    status,
    error,
    resolvedDomain,
    presentation,
  };
}


function hasActivityScope({
  companyUuid,
  activityUuid,
  activityType,
}) {
  return (
    isNonEmptyString(
      companyUuid
    )
    && isNonEmptyString(
      activityUuid
    )
    && isNonEmptyString(
      activityType
    )
  );
}


export function mapErrorToHostState(
  error
) {
  const codes =
    collectErrorCodes(
      error
    );


  if (
    codes.has(
      "NO_ACTIVE_PUBLICATION"
    )
  ) {
    return POS_ACTIVITY_HOST_STATES
      .NO_ACTIVE_PUBLICATION;
  }


  if (
    codes.has(
      "PERMISSION_DENIED"
    )
    || codes.has("FORBIDDEN")
  ) {
    return POS_ACTIVITY_HOST_STATES
      .PERMISSION_DENIED;
  }


  if (
    codes.has(
      "FEATURE_UNAVAILABLE"
    )
    || codes.has(
      "SUBSCRIPTION_UNAVAILABLE"
    )
    || codes.has(
      "SUBSCRIPTION_REQUIRED"
    )
  ) {
    return POS_ACTIVITY_HOST_STATES
      .FEATURE_UNAVAILABLE;
  }


  if (
    codes.has(
      "SESSION_CLOSED"
    )
  ) {
    return POS_ACTIVITY_HOST_STATES
      .SESSION_CLOSED;
  }


  if (
    codes.has(
      "UNSUPPORTED_ACTIVITY"
    )
    || codes.has(
      "DOMAIN_NOT_REGISTERED"
    )
  ) {
    return POS_ACTIVITY_HOST_STATES
      .UNSUPPORTED_ACTIVITY;
  }


  if (
    codes.has(
      "PUBLICATION_SCOPE_MISMATCH"
    )
    || codes.has(
      "PUBLICATION_INCOMPATIBLE"
    )
    || codes.has(
      "INVALID_ACTIVE_PUBLICATION"
    )
  ) {
    return POS_ACTIVITY_HOST_STATES
      .INVALID_PUBLICATION;
  }


  return POS_ACTIVITY_HOST_STATES
    .RUNTIME_ERROR;
}


function collectErrorCodes(
  error
) {
  const codes =
    new Set();

  let current = error;
  let depth = 0;

  while (
    current
    && depth < 8
  ) {
    if (
      typeof current.code
        === "string"
      && current.code.trim()
        !== ""
    ) {
      codes.add(
        current.code.trim()
      );
    }

    current =
      current.cause;

    depth += 1;
  }

  return codes;
}


function freezeArray(
  value
) {
  if (!Array.isArray(value)) {
    return Object.freeze([]);
  }

  return Object.freeze([
    ...value,
  ]);
}


function createPresentationError(
  code,
  message
) {
  const error =
    new Error(message);

  error.name =
    "POSActivityHostError";

  error.code = code;

  return error;
}


function isNonEmptyString(
  value
) {
  return (
    typeof value === "string"
    && value.trim() !== ""
  );
}


POSActivityHost.propTypes = {
  resolver: PropTypes.shape({
    resolvePOSActivityDomain:
      PropTypes.func.isRequired,
  }).isRequired,

  companyUuid:
    PropTypes.string,

  activityUuid:
    PropTypes.string,

  activityType:
    PropTypes.string,

  permissions:
    PropTypes.array,

  features:
    PropTypes.array,

  subscription:
    PropTypes.any,

  activePublication:
    PropTypes.object,

  renderState:
    PropTypes.func,

  onResolved:
    PropTypes.func,

  onError:
    PropTypes.func,
};


POSActivityWorkspace.propTypes = {
  resolvedDomain:
    PropTypes.object.isRequired,

  presentation:
    PropTypes.shape({
      workspaceDescriptor:
        PropTypes.object.isRequired,

      navigation:
        PropTypes.array.isRequired,

      actions:
        PropTypes.array.isRequired,

      component:
        PropTypes.any.isRequired,
    }).isRequired,
};


DefaultPOSActivityHostState.propTypes = {
  status:
    PropTypes.string.isRequired,

  error:
    PropTypes.object,
};


export {
  DefaultPOSActivityHostState,
  POSActivityWorkspace,
  materializePresentation,
};
